import { Form, FormProps, Input, Modal, ModalProps, Typography } from 'antd';
import isEqual from 'lodash.isequal';
import { ICompany } from '../../../interfaces/companies';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ButtonListFooterWithDeleteModalSM } from '../../../pages/components/buttonsList';
import {
  bankRulesSimple,
  clearFieldErrorMessageOnChange,
  companyRules,
  currencyRulesSimple,
} from '../../../utils/form.constants';
import { useNotificationOkCancel } from '../../../utils/hooks/useNotificationOkCancel.hook';
import { confirmModalDeleteProps, confirmModalUnsavedChangesProps } from '../../../pages/components/constants';
import { IBankAccount } from '../../../interfaces/bankAccounts';
import ICurrency from '../../../pages/currencies/currency.interface';
import { IBank } from '../../../interfaces/banks';
import { AutoComplete, AutoCompleteBankInput } from '../../inputs/autocomplete';
import { BaseRecord } from '@refinedev/core';
import {
  abaRoutingNumberRules,
  accountNumberRules,
  bankStateBranchNumberRules,
  branchNumberRules,
  ibanRules,
  intermediaryAccountNumberRules,
  isBankAccountFieldOptional,
  isIbanRequiredByCountryAndCurrency,
} from '../../../pages/payment-request/addAccount.constants';
import { intermediaryBankNameRules, intermediarySwiftRules } from '../../../pages/payment-request/addBank.constants';
import { useUpdateBankAccount } from '../../../utils/hooks/useUpdateBankAccount.hook';
import { useBankAccount } from '../../../utils/hooks/useBankAccount.hook';
import { Option } from '../../inputs/autocomplete/autocomplete';
import { mapCompanyItem, mapCurrencyItem } from '../../../utils/edit.constants';
import { useDeleteBankAccount } from '../../../utils/hooks/useDeleteBankAccount.hook';
import { DownOutlined } from '@ant-design/icons';

export type IBankAccountEdit = Omit<IBankAccount, 'bank'>;

interface IProps {
  formProps: FormProps;
  modalProps?: ModalProps;
  closeModal: () => void;
  editingId?: number;
  changeFieldData: (record: BaseRecord | undefined) => void;
  refetchList: () => void;
  show: () => void;
  fieldData?: BaseRecord;
}

export const EditBankAccount = ({
  modalProps,
  formProps,
  fieldData,
  changeFieldData,
  closeModal,
  refetchList,
  show,
}: IProps) => {
  const [currencyData, setCurrencyData] = useState<ICurrency>();
  const [companyData, setCompanyData] = useState<ICompany>();
  const [bankData, setBankData] = useState<IBank>();
  const [bankAccountData, setBankAccountData] = useState<IBankAccount>();
  const { data: loadData, isLoading: loadIsLoading } = useBankAccount(fieldData);
  const { update, isLoading: updateIsLoading } = useUpdateBankAccount(Number(fieldData?.id));
  const [loadedFormValues, setLoadedFormValues] = useState({});

  const { deleteBankAccount } = useDeleteBankAccount();

  const onOkDelete = useCallback(async () => {
    if (fieldData && fieldData.id && typeof fieldData.id === 'number') {
      changeFieldData(undefined);
      await deleteBankAccount(fieldData.id);
      closeModal();
      refetchList();
    }
  }, [changeFieldData, closeModal, deleteBankAccount, fieldData, refetchList]);

  const { openNotification, contextHolder: deleteContextHolder } = useNotificationOkCancel({
    ...confirmModalDeleteProps,
    onOk: onOkDelete,
  });

  useEffect(() => {
    if (loadData) {
      const currency: ICurrency = {
        name: '',
        code: loadData.bankAccountData.currencyCode,
      };
      formProps?.form?.setFieldsValue({
        bankId: loadData.bankData.name,
        currencyCode: loadData.bankAccountData.currencyCode,
        accountNumber: loadData.bankAccountData.accountNumber,
        abaRoutingNumber: loadData.bankAccountData.abaRoutingNumber,
        bankStateBranchNumber: loadData.bankAccountData.bankStateBranchNumber,
        branchNumber: loadData.bankAccountData.branchNumber,
        iban: loadData.bankAccountData.iban,
        companyId: loadData.companyData.name,
        correspondentBank: loadData.bankAccountData.correspondentBank,
        correspondentBankSwift: loadData.bankAccountData.correspondentBankSwift,
        correspondentBankAccountNumber: loadData.bankAccountData.correspondentBankAccountNumber,
      });
      setBankAccountData(loadData.bankAccountData);
      setBankData(loadData.bankData);
      setCompanyData(loadData.companyData);
      setCurrencyData(currency);
      setLoadedFormValues(formProps?.form?.getFieldsValue());
    } else {
      closeModal();
    }
  }, [loadData, fieldData, closeModal, formProps?.form]);

  useEffect(() => {
    if (fieldData?.id !== undefined) {
      show();
    } else {
      closeModal();
    }
  }, [fieldData?.id, closeModal, show]);

  const onUnsavedChangesOk = useCallback(() => {
    formProps?.form?.resetFields();
    changeFieldData(undefined);
    closeModal();
    setCurrencyData(undefined);
    setCompanyData(undefined);
    setBankData(undefined);
    setBankAccountData(undefined);
  }, [changeFieldData, closeModal, formProps?.form]);

  const onSubmit = useCallback(
    async (formValues: IBankAccountEdit) => {
      if (!bankData?.id || !companyData?.id || !currencyData?.code) {
        return;
      }
      const isSuccess = await update({
        id: bankAccountData?.id,
        bankId: bankData.id,
        currencyCode: currencyData.code,
        accountNumber: formValues.accountNumber,
        abaRoutingNumber: formValues.abaRoutingNumber,
        bankStateBranchNumber: formValues.bankStateBranchNumber,
        branchNumber: formValues.branchNumber,
        iban: formValues.iban,
        companyId: companyData.id,
        correspondentBank: formValues.correspondentBank,
        correspondentBankSwift: formValues.correspondentBankSwift,
        correspondentBankAccountNumber: formValues.correspondentBankAccountNumber,
      });

      if (isSuccess) {
        refetchList();
        onUnsavedChangesOk();
        setCurrencyData(undefined);
        setCompanyData(undefined);
        setBankData(undefined);
        setBankAccountData(undefined);
        closeModal();
      }
    },
    [
      bankData?.id,
      companyData?.id,
      currencyData?.code,
      update,
      bankAccountData?.id,
      refetchList,
      onUnsavedChangesOk,
      closeModal,
    ],
  );

  const changeBankData = useCallback(
    (data: IBank) => {
      if (data.id !== bankData?.id) {
        setBankData(data);
        formProps?.form?.setFieldValue('bankId', data.name);
      }
    },
    [bankData, formProps?.form],
  );

  const onSelectBank = useCallback((_: IBank, { data }: Option<IBank>) => changeBankData(data), [changeBankData]);

  const clearBankData = () => {
    setBankData(undefined);
  };

  const clearCurrencyData = () => {
    setCurrencyData(undefined);
  };

  const changeCompanyData = useCallback(
    (data: ICompany) => {
      if (data.id !== companyData?.id) {
        setCompanyData(data);
        formProps?.form?.setFieldValue('companyId', data.name);
        formProps?.form?.validateFields(['companyId']);
      }
    },
    [companyData, formProps?.form],
  );

  const onSelectCompany = useCallback(
    (_: ICompany, { data }: Option<ICompany>) => {
      changeCompanyData(data);
    },
    [changeCompanyData],
  );

  const clearCompanyData = () => {
    setCompanyData(undefined);
  };

  const { openNotification: openUnsavedChangesNotification, contextHolder: unsavedChangesContextHolder } =
    useNotificationOkCancel({
      ...confirmModalUnsavedChangesProps,
      onOk: onUnsavedChangesOk,
    });

  const onModalCancel = useCallback(() => {
    const isFormChanged = !!formProps && !isEqual(formProps.form?.getFieldsValue(), loadedFormValues);
    if (isFormChanged) {
      openUnsavedChangesNotification();
    } else {
      onUnsavedChangesOk();
    }
  }, [formProps, loadedFormValues, openUnsavedChangesNotification, onUnsavedChangesOk]);

  const onSave = () => {
    formProps.form?.submit();
  };

  useEffect(() => {
    if (!bankData) {
      formProps.form?.resetFields(['abaRoutingNumber', 'bankStateBranchNumber', 'branchNumber', 'iban']);
    } else {
      if (bankData.country === 'US') {
        formProps.form?.resetFields(['bankStateBranchNumber', 'branchNumber', 'iban']);
      } else if (bankData.country === 'AU') {
        formProps.form?.resetFields(['abaRoutingNumber', 'branchNumber', 'iban']);
      } else if (isBankAccountFieldOptional(bankData.country, currencyData?.code)) {
        formProps.form?.resetFields(['abaRoutingNumber', 'bankStateBranchNumber', 'branchNumber', 'iban']);
      } else {
        formProps.form?.resetFields(['abaRoutingNumber', 'bankStateBranchNumber']);
      }
    }
  }, [bankData, currencyData, formProps.form]);

  const numberField = useMemo(() => {
    return (
      <>
        <Form.Item name='abaRoutingNumber' rules={abaRoutingNumberRules(bankData?.country)}>
          <Input placeholder='ABA routing number' disabled={bankData?.country !== 'US'} />
        </Form.Item>
        <div style={{ marginTop: '-16px', display: 'flex', justifyContent: 'start', marginBottom: '16px' }}>
          <Typography.Text
            style={{
              color: '#F28A28',
              fontSize: 12,
              fontWeight: 400,
              lineHeight: '16px',
            }}>
            {
              'If not available, please key in 000000.'
            }
          </Typography.Text>
        </div>
        <Form.Item name='bankStateBranchNumber' rules={bankStateBranchNumberRules(bankData?.country)}>
          <Input placeholder='Bank State Branch number' disabled={bankData?.country !== 'AU'} />
        </Form.Item>
        <div style={{ marginTop: '-16px', display: 'flex', justifyContent: 'start', marginBottom: '16px' }}>
          <Typography.Text
            style={{
              color: '#F28A28',
              fontSize: 12,
              fontWeight: 400,
              lineHeight: '16px',
            }}>
            {
              'If not available, please key in 000000.'
            }
          </Typography.Text>
        </div>
        <Form.Item name='branchNumber' rules={branchNumberRules(bankData?.country, currencyData?.code)}>
          <Input
            placeholder='Branch number'
            disabled={
              bankData?.country === 'AU' ||
              bankData?.country === 'US' ||
              isBankAccountFieldOptional(bankData?.country, currencyData?.code) ||
              isIbanRequiredByCountryAndCurrency(bankData?.country, currencyData?.code)
            }
          />
        </Form.Item>
        <div style={{ marginTop: '-16px', display: 'flex', justifyContent: 'start', marginBottom: '16px' }}>
          <Typography.Text
            style={{
              color: '#F28A28',
              fontSize: 12,
              fontWeight: 400,
              lineHeight: '16px',
            }}>
            {
              'If not available, please key in 000000.'
            }
          </Typography.Text>
        </div>
      </>
    );
  }, [bankData, currencyData]);

  const ibanField = useMemo(() => {
    return (
      <Form.Item name='iban' rules={ibanRules(bankData?.country, currencyData?.code)}>
        <Input
          placeholder='IBAN'
          disabled={!isIbanRequiredByCountryAndCurrency(bankData?.country, currencyData?.code)}
        />
      </Form.Item>
    );
  }, [bankData, currencyData]);

  if (fieldData?.id === undefined) {
    return null;
  }

  return (
    <>
      <Modal
        {...modalProps}
        title={`Edit account #${fieldData?.id}`}
        className='smartModalTitle'
        onCancel={onModalCancel}
        footer={ButtonListFooterWithDeleteModalSM(onSave, onModalCancel, openNotification)}
      >
        {unsavedChangesContextHolder}
        {deleteContextHolder}
        <Form
          {...formProps}
          layout='vertical'
          onFinish={onSubmit}
          disabled={loadIsLoading || updateIsLoading}
          onValuesChange={clearFieldErrorMessageOnChange(formProps?.form)}
        >
          <Form.Item name='bankId' rules={bankRulesSimple(bankData)}>
            <AutoCompleteBankInput onSelect={onSelectBank} allowClear refetchOnEmptyValue onClear={clearBankData} />
          </Form.Item>
          <Form.Item name='currencyCode' rules={currencyRulesSimple(currencyData)}>
            <AutoComplete
              allowClear
              resource='api/v1/currencies'
              mapItem={mapCurrencyItem}
              onSelect={(_, { data }) => setCurrencyData(data)}
              onClear={clearCurrencyData}
              refetchOnEmptyValue
            >
              <Input placeholder='Currency' size='large' suffix={<DownOutlined />} />
            </AutoComplete>
          </Form.Item>
          <Form.Item name='accountNumber' rules={accountNumberRules}>
            <Input placeholder='Account number' type='text' />
          </Form.Item>
          <Form.Item name='companyId' rules={companyRules(companyData)}>
            <AutoComplete
              resource='api/v1/companies'
              mapItem={mapCompanyItem}
              onSelect={onSelectCompany}
              allowClear
              refetchOnEmptyValue
              onClear={clearCompanyData}
            >
              <Input placeholder='Provider' size='large' suffix={<DownOutlined />} />
            </AutoComplete>
          </Form.Item>
          {numberField}
          {ibanField}
          <Form.Item name='correspondentBank' rules={intermediaryBankNameRules}>
            <Input placeholder='Correspondent bank' />
          </Form.Item>
          <Form.Item name='correspondentBankSwift' rules={intermediarySwiftRules}>
            <Input placeholder='Correspondent bank SWIFT' />
          </Form.Item>
          <Form.Item name='correspondentBankAccountNumber' rules={intermediaryAccountNumberRules}>
            <Input placeholder='Correspondent bank account' />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};
